import {FC, Fragment, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Check,
    Close,
    Edit,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';
import {
    AdminMissionDto,
    AdminMissionTierDto,
    MissionCategory,
    MissionRewardCostumeDto,
} from '../../types/mission';
import {
    useMissions,
    usePatchMission,
    usePatchMissionTier,
    useRewardCostumes,
} from '../../hooks/useMissions';

// ── 레이블/색상 상수 ─────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
    LEARNING: '학습',
    COLLECTION: '수집',
    SOCIAL: '소셜',
    STREAK: '연속',
    ACHIEVEMENT: '달성',
};

const CATEGORY_COLOR: Record<
    string,
    'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' | 'default'
> = {
    LEARNING: 'primary',
    COLLECTION: 'info',
    SOCIAL: 'success',
    STREAK: 'warning',
    ACHIEVEMENT: 'secondary',
};

// ── 단계(Tier) 행 ─────────────────────────────────────────────────────────────

interface TierRowProps {
    tier: AdminMissionTierDto;
    rewardCostumes: MissionRewardCostumeDto[];
}

const TierRow: FC<TierRowProps> = ({tier, rewardCostumes}) => {
    const patchTierMut = usePatchMissionTier();

    // 깃털 인라인 편집 상태
    const [editingXp, setEditingXp] = useState(false);
    const [xpInput, setXpInput] = useState(String(tier.rewardXp));

    // 스티커 선택 상태 (0 = "없음")
    const [costumeId, setCostumeId] = useState<number>(
        tier.rewardCostumeId ?? 0
    );

    const handleXpSave = async () => {
        const newXp = Number(xpInput);
        if (isNaN(newXp) || newXp < 0) {
            alert('유효한 깃털 수를 입력하세요.');
            return;
        }
        if (newXp === tier.rewardXp) {
            setEditingXp(false);
            return;
        }
        try {
            await patchTierMut.mutateAsync({
                tierId: tier.id,
                body: {rewardXp: newXp},
            });
            setEditingXp(false);
        } catch (e: unknown) {
            alert(`저장 실패: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    const handleXpCancel = () => {
        setXpInput(String(tier.rewardXp));
        setEditingXp(false);
    };

    const handleCostumeChange = async (newVal: number) => {
        setCostumeId(newVal);
        try {
            if (newVal === 0) {
                await patchTierMut.mutateAsync({
                    tierId: tier.id,
                    body: {clearCostume: true},
                });
            } else {
                await patchTierMut.mutateAsync({
                    tierId: tier.id,
                    body: {rewardCostumeId: newVal},
                });
            }
        } catch (e: unknown) {
            // 실패 시 원래 값으로 복구
            setCostumeId(tier.rewardCostumeId ?? 0);
            alert(`저장 실패: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    const currentCostume = rewardCostumes.find((c) => c.id === costumeId);

    return (
        <TableRow sx={{bgcolor: 'action.hover'}}>
            {/* 단계 */}
            <TableCell sx={{pl: 6}}>
                <Chip
                    label={`Tier ${tier.tierIndex + 1}`}
                    size="small"
                    variant="outlined"
                    color="default"
                />
            </TableCell>
            {/* 목표값 */}
            <TableCell>
                <Typography variant="body2" fontWeight={600}>
                    {tier.threshold.toLocaleString()}
                </Typography>
            </TableCell>
            {/* 보상 타입 */}
            <TableCell>
                <Chip
                    label={tier.rewardType}
                    size="small"
                    color={
                        tier.rewardType === 'XP'
                            ? 'info'
                            : tier.rewardType === 'COSTUME'
                              ? 'success'
                              : 'secondary'
                    }
                />
            </TableCell>
            {/* 깃털 (인라인 편집) */}
            <TableCell>
                {editingXp ? (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <TextField
                            type="number"
                            size="small"
                            value={xpInput}
                            onChange={(e) => setXpInput(e.target.value)}
                            sx={{width: 90}}
                            inputProps={{min: 0}}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleXpSave();
                                if (e.key === 'Escape') handleXpCancel();
                            }}
                        />
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleXpSave}
                            disabled={patchTierMut.isPending}
                        >
                            <Check fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleXpCancel}
                            disabled={patchTierMut.isPending}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="body2">
                            {tier.rewardXp.toLocaleString()}
                        </Typography>
                        <Tooltip title="깃털 수 수정">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setXpInput(String(tier.rewardXp));
                                    setEditingXp(true);
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </TableCell>
            {/* 연결 스티커 */}
            <TableCell>
                <Stack direction="row" alignItems="center" gap={1}>
                    {currentCostume?.imageUrl && (
                        <Box
                            component="img"
                            src={currentCostume.imageUrl}
                            alt={currentCostume.nameKo}
                            sx={{
                                width: 32,
                                height: 32,
                                objectFit: 'contain',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        />
                    )}
                    <Select
                        size="small"
                        value={costumeId}
                        onChange={(e) => handleCostumeChange(Number(e.target.value))}
                        disabled={patchTierMut.isPending}
                        sx={{minWidth: 180}}
                        renderValue={(val) => {
                            if (val === 0) return <em>없음</em>;
                            const found = rewardCostumes.find((c) => c.id === val);
                            return found ? found.nameKo : `ID ${val}`;
                        }}
                    >
                        <MenuItem value={0}>
                            <em>없음 (해제)</em>
                        </MenuItem>
                        {rewardCostumes.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    {c.imageUrl && (
                                        <Box
                                            component="img"
                                            src={c.imageUrl}
                                            alt={c.nameKo}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                objectFit: 'contain',
                                            }}
                                        />
                                    )}
                                    <Typography variant="body2">
                                        {c.nameKo}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        ({c.code})
                                    </Typography>
                                </Stack>
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
            </TableCell>
            {/* 획득자 수 */}
            <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                    {tier.claimCount.toLocaleString()}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

// ── 미션 행 ───────────────────────────────────────────────────────────────────

interface MissionRowProps {
    mission: AdminMissionDto;
    rewardCostumes: MissionRewardCostumeDto[];
}

const MissionRow: FC<MissionRowProps> = ({mission, rewardCostumes}) => {
    const patchMissionMut = usePatchMission();
    const [expanded, setExpanded] = useState(false);

    const handleToggleActive = async (checked: boolean) => {
        try {
            await patchMissionMut.mutateAsync({
                id: mission.id,
                body: {isActive: checked},
            });
        } catch (e: unknown) {
            alert(`저장 실패: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    return (
        <Fragment>
            <TableRow hover>
                {/* 카테고리 */}
                <TableCell>
                    <Chip
                        label={CATEGORY_LABEL[mission.category] ?? mission.category}
                        size="small"
                        color={CATEGORY_COLOR[mission.category] ?? 'default'}
                    />
                </TableCell>
                {/* code */}
                <TableCell>
                    <Typography
                        variant="body2"
                        component="code"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            bgcolor: 'action.hover',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                        }}
                    >
                        {mission.code}
                    </Typography>
                </TableCell>
                {/* 이름 */}
                <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                        {mission.nameKo}
                    </Typography>
                </TableCell>
                {/* scope */}
                <TableCell>
                    <Typography variant="caption" color="text.secondary">
                        {mission.scope}
                    </Typography>
                </TableCell>
                {/* structure */}
                <TableCell>
                    <Chip
                        label={mission.structure}
                        size="small"
                        variant="outlined"
                    />
                </TableCell>
                {/* metricType */}
                <TableCell>
                    <Typography variant="caption" color="text.secondary">
                        {mission.metricType}
                    </Typography>
                </TableCell>
                {/* 활성 */}
                <TableCell>
                    <Switch
                        checked={mission.isActive}
                        onChange={(e) => handleToggleActive(e.target.checked)}
                        disabled={patchMissionMut.isPending}
                        size="small"
                    />
                </TableCell>
                {/* 단계 수 + 펼치기 */}
                <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="body2">
                            {mission.tiers.length}
                        </Typography>
                        {mission.tiers.length > 0 && (
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                aria-label="단계 펼치기"
                            >
                                {expanded ? (
                                    <ExpandLess fontSize="small" />
                                ) : (
                                    <ExpandMore fontSize="small" />
                                )}
                            </IconButton>
                        )}
                    </Stack>
                </TableCell>
            </TableRow>

            {/* 단계 상세 (accordion) */}
            {mission.tiers.length > 0 && (
                <TableRow>
                    <TableCell colSpan={8} sx={{p: 0, borderBottom: 0}}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Table size="small">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            bgcolor: 'background.default',
                                        }}
                                    >
                                        <TableCell sx={{pl: 6, width: 120}}>
                                            단계
                                        </TableCell>
                                        <TableCell width={120}>
                                            목표값
                                        </TableCell>
                                        <TableCell width={140}>
                                            보상 타입
                                        </TableCell>
                                        <TableCell width={180}>
                                            깃털 (XP)
                                        </TableCell>
                                        <TableCell>연결 스티커</TableCell>
                                        <TableCell align="right" width={100}>
                                            획득자 수
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mission.tiers
                                        .slice()
                                        .sort(
                                            (a, b) =>
                                                a.tierIndex - b.tierIndex
                                        )
                                        .map((tier) => (
                                            <TierRow
                                                key={tier.id}
                                                tier={tier}
                                                rewardCostumes={rewardCostumes}
                                            />
                                        ))}
                                </TableBody>
                            </Table>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────

export const MissionList: FC = () => {
    const {data: missions, isLoading, error} = useMissions();
    const {data: rewardCostumes} = useRewardCostumes();

    // 카테고리별 정렬: sortOrder 오름차순
    const sortedMissions = useMemo(() => {
        if (!missions) return [];
        return [...missions].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
        );
    }, [missions]);

    // 카테고리 목록 (표시 순서)
    const categories = useMemo(() => {
        const seen = new Set<string>();
        const result: MissionCategory[] = [];
        for (const m of sortedMissions) {
            if (!seen.has(m.category)) {
                seen.add(m.category);
                result.push(m.category as MissionCategory);
            }
        }
        return result;
    }, [sortedMissions]);

    const costumeList = rewardCostumes ?? [];

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    미션 목록을 불러올 수 없습니다. 잠시 후 다시 시도하세요.
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box mb={3}>
                <Typography variant="h4">미션 관리</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                    미션 활성 여부를 토글하거나, 단계별 깃털 수/연결 스티커를
                    수정할 수 있습니다. 미션 생성은 지원하지 않습니다.
                </Typography>
            </Box>

            {categories.map((category) => {
                const categoryMissions = sortedMissions.filter(
                    (m) => m.category === category
                );
                return (
                    <Box key={category} mb={3}>
                        <Typography
                            variant="h6"
                            mb={1}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Chip
                                label={
                                    CATEGORY_LABEL[category] ?? category
                                }
                                color={CATEGORY_COLOR[category] ?? 'default'}
                                size="small"
                            />
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                            >
                                {categoryMissions.length}개
                            </Typography>
                        </Typography>

                        <Card>
                            <CardContent sx={{p: 0}}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width={110}>
                                                    카테고리
                                                </TableCell>
                                                <TableCell width={220}>
                                                    코드
                                                </TableCell>
                                                <TableCell>이름(KO)</TableCell>
                                                <TableCell width={110}>
                                                    범위
                                                </TableCell>
                                                <TableCell width={110}>
                                                    구조
                                                </TableCell>
                                                <TableCell width={160}>
                                                    메트릭
                                                </TableCell>
                                                <TableCell width={80}>
                                                    활성
                                                </TableCell>
                                                <TableCell width={100}>
                                                    단계 수
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {categoryMissions.map((m) => (
                                                <MissionRow
                                                    key={m.id}
                                                    mission={m}
                                                    rewardCostumes={costumeList}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Box>
                );
            })}

            {sortedMissions.length === 0 && !isLoading && (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="30vh"
                >
                    <Typography color="text.secondary">
                        미션이 없습니다.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
